/**
 * Copyright 2024 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { useProductShelfResults } from '@/features/category/api/getProductShelfResults';
import { useProductShelfACESResults } from '@/features/category/api/getProductShelfACESResults';
import { useSearchProductData } from '@/features/search/api/getSearchProductData';
import { usePageType } from '@/hooks/usePageType';
import { pageTypes } from '@/constants/page';
import { useProductList } from '@/features/shelf/context/useProductList';
import { useBrandProductData } from '@/features/brands/api/getProductBrandData';
import { type QueryStatus } from '@tanstack/react-query';
import { useLegacyRouter } from '@/hooks/useLegacyRouter';
import { useProductDetails } from '@/features/product/api/getProductDetails';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';
import { useACESBrandProductData } from '@/features/brands/api/getAcesProductBrandData';
import { useMerchShelfResults } from '@/features/category/api/getMerchShelfResults';

type UseCombinedInfiniteQueryResult = {
  totalRecords: number | undefined;
  hasNextPage: boolean;
  pageNumber: number | undefined;
  fetchNextPage: undefined | (() => Promise<unknown>);
  infiniteQueryStatus: QueryStatus | undefined;
  infiniteQueryIsPending: boolean;
  isFetchingNextPage: boolean;
  currentlyShowing: number | undefined;
};

export const useCombinedInfiniteQuery = (): UseCombinedInfiniteQueryResult => {
  const { acesEnabled } = useAcesFlags();
  const { pageType } = usePageType();
  const router = useLegacyRouter();
  const isShelfPage = pageType === pageTypes.ProductShelf;
  const isSearchPage = pageType === pageTypes.search;
  const isBrandPage = pageType === pageTypes.BrandShelf;
  const isMerchShelfPage =
    router.route === '/deals/[cmsPath]/[[...slug]]' || pageType === pageTypes.MerchShelf;
  const isPDPPage = router.pathname === '/products/[...slug]';

  const { data: shelfData } = useProductList();
  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const shelfQuery = useShelfResultsFunction({
    enabled: isShelfPage || (isMerchShelfPage && !acesEnabled),
  });
  const merchShelfQuery = useMerchShelfResults({ enabled: isMerchShelfPage && acesEnabled });
  const searchQuery = useSearchProductData({ enabled: isSearchPage });
  const useBrandProductDataFunction = acesEnabled ? useACESBrandProductData : useBrandProductData;
  const brandQuery = useBrandProductDataFunction({
    enabled: isBrandPage,
  });
  const pdpQuery = useProductDetails({ enabled: false });

  const currentlyShowing = isPDPPage ? 1 : shelfData?.shelfParts?.length;

  if (isShelfPage || (isMerchShelfPage && !acesEnabled)) {
    return {
      totalRecords: shelfQuery.data?.productShelfResults?.totalNumberOfRecords,
      hasNextPage: shelfQuery.hasNextPage,
      pageNumber: shelfQuery.pageNumber,
      fetchNextPage: shelfQuery.fetchNextPage,
      infiniteQueryStatus: shelfQuery.status,
      infiniteQueryIsPending: shelfQuery.isPending,
      isFetchingNextPage: shelfQuery.isFetchingNextPage,
      currentlyShowing,
    };
  }
  if (isSearchPage) {
    return {
      totalRecords: searchQuery.data?.total,
      hasNextPage: searchQuery.hasNextPage,
      pageNumber: searchQuery.pageNumber,
      fetchNextPage: searchQuery.fetchNextPage,
      infiniteQueryStatus: searchQuery.status,
      infiniteQueryIsPending: searchQuery.isPending,
      isFetchingNextPage: searchQuery.isFetchingNextPage,
      currentlyShowing,
    };
  }
  if (isBrandPage) {
    return {
      totalRecords: brandQuery.data?.total,
      hasNextPage: brandQuery.hasNextPage,
      pageNumber: brandQuery.pageNumber,
      fetchNextPage: brandQuery.fetchNextPage,
      infiniteQueryStatus: brandQuery.status,
      infiniteQueryIsPending: brandQuery.isPending,
      isFetchingNextPage: brandQuery.isFetchingNextPage,
      currentlyShowing,
    };
  }
  if (isMerchShelfPage && acesEnabled) {
    return {
      totalRecords: merchShelfQuery.data?.merchShelfResults?.totalNumberOfRecords,
      hasNextPage: merchShelfQuery.hasNextPage,
      pageNumber: merchShelfQuery.pageNumber,
      fetchNextPage: merchShelfQuery.fetchNextPage,
      infiniteQueryStatus: merchShelfQuery.status,
      infiniteQueryIsPending: merchShelfQuery.isPending,
      isFetchingNextPage: merchShelfQuery.isFetchingNextPage,
      currentlyShowing,
    };
  }

  if (isPDPPage) {
    return {
      totalRecords: pdpQuery.data ? 1 : 0,
      hasNextPage: false,
      pageNumber: undefined,
      fetchNextPage: undefined,
      infiniteQueryStatus: pdpQuery.status,
      infiniteQueryIsPending: pdpQuery.isPending,
      isFetchingNextPage: false,
      currentlyShowing,
    };
  }

  return {
    totalRecords: undefined,
    hasNextPage: false,
    pageNumber: undefined,
    fetchNextPage: undefined,
    infiniteQueryStatus: undefined,
    infiniteQueryIsPending: false,
    isFetchingNextPage: false,
    currentlyShowing: undefined,
  };
};
